import React, {useState, useEffect} from 'react';
import {Search, Plus, X, Check, Loader, AlertCircle, ChevronLeft, ChevronRight} from 'lucide-react';
import {api} from '../../config/api';
import secureStorage from '../../utils/secureStorage';
import {URLS_UPLOADS} from '../../config/url';

/* ─── Tiny color picker (same as ReferenceManager) ───── */
const PRESET_COLORS = [
    '#4DB6AC', '#26A69A', '#009688', '#00897B', '#00796B',
    '#EF5350', '#EC407A', '#AB47BC', '#7E57C2', '#5C6BC0',
    '#42A5F5', '#29B6F6', '#26C6DA', '#66BB6A', '#9CCC65',
    '#FFEE58', '#FFCA28', '#FFA726', '#FF7043', '#78909C',
];

const MiniColorPicker = ({value, onChange}) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative inline-block">
            <button type="button" onClick={() => setOpen(o => !o)}
                    className="w-7 h-7 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm"
                    style={{backgroundColor: value}}/>
            {open && (
                <div
                    className="absolute z-50 mt-1 left-0 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-2 w-44">
                    <div className="grid grid-cols-5 gap-1">
                        {PRESET_COLORS.map(c => (
                            <button key={c} type="button" onClick={() => {
                                onChange(c);
                                setOpen(false);
                            }}
                                    className={`w-7 h-7 rounded-md hover:scale-110 transition-transform ${c === value ? 'ring-2 ring-primary' : ''}`}
                                    style={{backgroundColor: c}}/>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

/* ─── Inline create form (shown inside the picker) ──── */
const InlineCreateForm = ({config, onCreated, onCancel}) => {
    const [form, setForm] = useState(() => {
        const init = {};
        config.fields.forEach(f => {
            init[f.key] = f.default ?? '';
        });
        return init;
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const set = (key, val) => setForm(prev => ({...prev, [key]: val}));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        for (const f of config.fields) {
            if (f.required && !form[f.key] && form[f.key] !== 0 && form[f.key] !== false) {
                setError(`"${f.label}" est obligatoire`);
                return;
            }
        }

        setSubmitting(true);
        try {
            const account = await secureStorage.getItem('selectedAccount');
            const headers = {'X-account-id': account.id, Accept: 'application/json'};

            let payload;
            if (config.useFormData) {
                payload = new FormData();
                config.fields.forEach(f => {
                    const val = form[f.key];
                    if (val !== undefined && val !== null) payload.append(f.key, val);
                });
                // Append extra hidden fields (not shown in form)
                if (config.extraData) {
                    Object.entries(config.extraData).forEach(([key, val]) => {
                        payload.append(key, String(val));
                    });
                }
                headers['Content-Type'] = undefined;
            } else {
                payload = {...(config.extraData || {})};
                config.fields.forEach(f => {
                    payload[f.key] = form[f.key];
                });
                headers['Content-Type'] = 'application/json';
            }

            const res = await api.post(config.endpoint, payload, {headers});

            // Normalize created entity — support custom getName (e.g. clients use firstName/lastName)
            const raw = res.data?.data || res.data || {};
            const idFromIri = typeof raw['@id'] === 'string' ? raw['@id'].split('/').pop() : null;
            const displayName = config.getName
                ? config.getName(raw)
                : (raw.name || `${form.first_name || ''} ${form.last_name || ''}`.trim() || form.name || '—');
            const created = {
                ...raw,
                id: raw.id || raw.uuid || idFromIri,
                name: displayName,
            };

            if (!created.id) throw new Error('ID manquant dans la réponse');
            onCreated(created);
        } catch (err) {
            setError(err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}
              className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 p-4 space-y-3">
            <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Créer {config.singularLabel}
                </p>
                <button type="button" onClick={onCancel}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg">
                    <X className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500"/>
                </button>
            </div>

            {error && (
                <div
                    className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs rounded-lg">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0"/>{error}
                </div>
            )}

            <div className="space-y-2.5">
                {config.fields.map(f => {
                    if (f.type === 'color') return (
                        <div key={f.key} className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 dark:text-gray-400">{f.label}:</span>
                            <MiniColorPicker value={form[f.key] || '#4DB6AC'} onChange={v => set(f.key, v)}/>
                        </div>
                    );
                    if (f.type === 'toggle') return (
                        <label key={f.key} className="flex items-center justify-between">
                            <span className="text-xs text-gray-600 dark:text-gray-400">{f.label}</span>
                            <button type="button" onClick={() => set(f.key, !form[f.key])}
                                    className={`relative w-9 h-5 rounded-full transition-colors ${form[f.key] ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                                <span
                                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form[f.key] ? 'translate-x-4' : ''}`}/>
                            </button>
                        </label>
                    );
                    if (f.type === 'textarea') return (
                        <textarea key={f.key} rows={2} value={form[f.key] || ''}
                                  onChange={e => set(f.key, e.target.value)}
                                  placeholder={f.placeholder || f.label}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"/>
                    );
                    return (
                        <input key={f.key} type={f.type === 'number' ? 'number' : 'text'}
                               step={f.step} min={f.min} max={f.max}
                               value={form[f.key] ?? ''}
                               onChange={e => set(f.key, f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                               placeholder={f.placeholder || f.label}
                               autoFocus={f.autoFocus}
                               className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"/>
                    );
                })}
            </div>

            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onCancel}
                        className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                    Annuler
                </button>
                <button type="submit" disabled={submitting}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5">
                    {submitting && <Loader className="w-3 h-3 animate-spin"/>}
                    {submitting ? 'Création...' : 'Créer & Sélectionner'}
                </button>
            </div>
        </form>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ReferencePickerModal
   Lets the user search, browse, optionally create, and select one or many
   reference entities. Used by both product forms and the calendar EventModal.

   Props
   ─────
   config          — one of the configs from referenceConfigs.js
   currentValue    — selected id (single) OR array of ids (multiSelect)
   initialSelected — (multiSelect only) array of full item objects to pre-select;
                     enables cross-page persistence of selected items
   onSelect        — single: (item) => void  |  multiSelect: (items[]) => void
   onClose         — () => void
   displayValue    — optional (item) => string override for display label
   multiSelect     — boolean (default false) — enables checkbox multi-select mode
   ═══════════════════════════════════════════════════════════════════════════ */
const ReferencePickerModal = ({
    config,
    currentValue,
    initialSelected = [],
    onSelect,
    onClose,
    displayValue,
    multiSelect = false,
}) => {
    const [searchTerm,     setSearchTerm]     = useState('');
    const [debouncedSearch,setDebouncedSearch]= useState('');
    const [items,          setItems]          = useState([]);
    const [isLoading,      setIsLoading]      = useState(true);
    const [error,          setError]          = useState(null);
    const [showCreate,     setShowCreate]     = useState(false);
    const [currentPage,    setCurrentPage]    = useState(1);
    const [totalPages,     setTotalPages]     = useState(1);
    // When config.searchParams is set (e.g. name vs barcode), lets the user pick which field to search on
    const [searchField,    setSearchField]    = useState(config.searchParams?.[0]?.key || config.searchParam || 'name');

    // Multi-select: track chosen IDs as a Set AND full objects as a Map
    // Map<string id, item object> enables cross-page persistence.
    const [selectedIds, setSelectedIds] = useState(() => {
        if (!multiSelect) return new Set();
        // Seed from initialSelected (array of objects) first, then currentValue
        if (initialSelected?.length) return new Set(initialSelected.map(s => String(s.id)));
        if (Array.isArray(currentValue)) return new Set(currentValue.map(String));
        if (currentValue)                return new Set([String(currentValue)]);
        return new Set();
    });
    const [selectedMap, setSelectedMap] = useState(() => {
        if (!multiSelect) return new Map();
        const m = new Map();
        (initialSelected || []).forEach(s => m.set(String(s.id), s));
        return m;
    });

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 250);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Re-run the search immediately when the user switches which field to search on
    useEffect(() => {
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchField]);

    // Fetch items from API
    useEffect(() => {
        let cancelled = false;
        const fetchItems = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const account = await secureStorage.getItem('selectedAccount');
                const sp  = searchField || config.searchParam || 'name';
                let url   = `${config.endpoint}?page=${currentPage}`;
                if (config.extraParams) {
                    Object.entries(config.extraParams).forEach(([key, val]) => {
                        url += `&${key}=${encodeURIComponent(val)}`;
                    });
                }
                if (debouncedSearch.trim()) url += `&${sp}=${encodeURIComponent(debouncedSearch.trim())}`;

                const res = await api.get(url, {
                    headers: {'X-account-id': account.id, Accept: 'application/json'},
                });
                if (cancelled) return;

                let data;
                if (res.data.pagination) {
                    setTotalPages(res.data.pagination.totalPages || 1);
                    data = res.data.data || [];
                } else if (res.data.items) {
                    setTotalPages(1);
                    data = res.data.items;
                } else {
                    setTotalPages(1);
                    data = Array.isArray(res.data) ? res.data : (res.data.data || []);
                }
                setItems(data);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        fetchItems();
        return () => { cancelled = true; };
    }, [config.endpoint, config.searchParam, searchField, currentPage, debouncedSearch]);

    /** After inline creation: prepend item to list and auto-select it */
    const handleCreated = (newItem) => {
        setItems(prev => [newItem, ...prev]);
        setShowCreate(false);
        if (multiSelect) {
            setSelectedIds(prev => new Set([...prev, String(newItem.id)]));
            setSelectedMap(prev => new Map([...prev, [String(newItem.id), newItem]]));
        } else {
            onSelect(newItem);
        }
    };

    /** Item click: immediate select (single) or toggle (multi) */
    const handleItemClick = (item) => {
        if (!multiSelect) {
            onSelect(item);
            return;
        }
        const key = String(item.id);
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else               next.add(key);
            return next;
        });
        setSelectedMap(prev => {
            const next = new Map(prev);
            if (next.has(key)) next.delete(key);
            else               next.set(key, item);
            return next;
        });
    };

    /** Confirm multi-select — call onSelect with ALL selected items (across pages) */
    const handleConfirm = () => {
        // Use selectedMap for cross-page persistence; fall back to filtering current items
        let selected;
        if (selectedMap.size > 0) {
            selected = Array.from(selectedMap.values());
        } else {
            selected = items.filter(item => selectedIds.has(String(item.id)));
        }
        onSelect(selected);
    };

    /** Resolve the display label for any item */
    const getDisplayLabel = (item) => {
        if (typeof displayValue === 'function') return displayValue(item);
        if (config.getName)                     return config.getName(item);
        return item.name || '—';
    };

    const canCreate = config.fields && config.fields.length > 0 && !config.disableCreate;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
            onClick={onClose}>
            <div
                className="w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-lg max-h-[85vh] flex flex-col overflow-hidden picker-animate"
                onClick={e => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex-1">
                        <h3 className="text-base font-semibold text-gray-800 dark:text-white">
                            {multiSelect ? `Choisir des ${config.pluralLabel}` : `Sélectionner ${config.singularLabel}`}
                        </h3>
                        {multiSelect && selectedIds.size > 0 && (
                            <p className="text-xs text-primary mt-0.5">
                                {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose}
                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                        <X className="w-4 h-4 text-gray-400 dark:text-gray-500"/>
                    </button>
                </div>

                {/* ── Search bar + optional Create button ── */}
                <div className="flex items-center gap-2 px-4 py-3">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"/>
                        <input
                            type="text"
                            placeholder={
                                config.searchParams?.find(s => s.key === searchField)?.placeholder || "Rechercher..."
                            }
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {canCreate && (
                        <button
                            onClick={() => setShowCreate(s => !s)}
                            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl transition-colors whitespace-nowrap ${
                                showCreate
                                    ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                                    : 'bg-primary text-white hover:bg-primary/90'
                            }`}
                        >
                            <Plus className="w-4 h-4"/>
                            <span className="hidden sm:inline">Créer</span>
                        </button>
                    )}
                </div>

                {/* ── Search field switch (e.g. Nom / Code-barres) ── */}
                {config.searchParams?.length > 1 && (
                    <div className="flex items-center gap-1.5 px-4 pb-3 -mt-1">
                        {config.searchParams.map(sp => (
                            <button
                                key={sp.key}
                                type="button"
                                onClick={() => setSearchField(sp.key)}
                                className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                                    searchField === sp.key
                                        ? 'bg-primary/10 dark:bg-primary/20 text-primary'
                                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            >
                                {sp.label}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Inline create form ── */}
                {showCreate && canCreate && (
                    <InlineCreateForm
                        config={config}
                        onCreated={handleCreated}
                        onCancel={() => setShowCreate(false)}
                    />
                )}

                {/* ── Items list ── */}
                <div className="flex-1 overflow-y-auto px-2 pb-2">
                    {isLoading ? (
                        <div className="flex justify-center py-10">
                            <Loader className="w-6 h-6 text-primary animate-spin"/>
                        </div>
                    ) : error ? (
                        <div className="py-6 text-center text-sm text-red-500 dark:text-red-400">{error}</div>
                    ) : items.length === 0 ? (
                        <div className="py-10 text-center">
                            <p className="text-sm text-gray-400 dark:text-gray-500">
                                Aucun(e) {config.singularLabel} trouvé(e)
                            </p>
                            {canCreate && !showCreate && (
                                <button onClick={() => setShowCreate(true)}
                                        className="mt-2 text-sm text-primary font-medium hover:underline">
                                    Créer {config.singularLabel}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {items.map(item => {
                                const isSelected = multiSelect
                                    ? selectedIds.has(String(item.id))
                                    : currentValue && String(item.id) === String(currentValue);

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleItemClick(item)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                                            isSelected
                                                ? 'bg-primary/10 dark:bg-primary/20 ring-1 ring-primary/30'
                                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }`}
                                    >
                                        {/* Avatar */}
                                        {config.renderAvatar ? (
                                            <div className="w-9 h-9 flex-shrink-0">
                                                {config.renderAvatar(item)}
                                            </div>
                                        ) : (
                                            <div
                                                className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold"
                                                style={{backgroundColor: item.color || '#4DB6AC'}}>
                                                {item.imageUrl ? (
                                                    <img
                                                        src={item.imageUrl.startsWith('http') ? item.imageUrl : `${URLS_UPLOADS}${item.imageUrl}`}
                                                        alt=""
                                                        className="w-full h-full object-cover rounded-lg"/>
                                                ) : (
                                                    (getDisplayLabel(item) || '??').slice(0, 2).toUpperCase()
                                                )}
                                            </div>
                                        )}

                                        {/* Label + subtitle */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                                                {getDisplayLabel(item)}
                                            </p>
                                            {config.renderSubtitle && (
                                                <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                                                    {config.renderSubtitle(item)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Selection indicator */}
                                        {multiSelect ? (
                                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                                isSelected
                                                    ? 'bg-primary border-primary'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}>
                                                {isSelected && <Check className="w-3 h-3 text-white"/>}
                                            </div>
                                        ) : (
                                            isSelected && (
                                                <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                                                    <Check className="w-3 h-3 text-white"/>
                                                </div>
                                            )
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center gap-1 pt-3 pb-1">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30">
                                <ChevronLeft className="w-3.5 h-3.5"/>
                            </button>
                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center px-2">
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30">
                                <ChevronRight className="w-3.5 h-3.5"/>
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Multi-select confirm footer ── */}
                {multiSelect && (
                    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                            {selectedIds.size > 0
                                ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? 's' : ''}`
                                : 'Aucune sélection'}
                        </span>
                        <button
                            onClick={handleConfirm}
                            className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors">
                            Valider
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                .picker-animate {
                    animation: pickerIn 0.25s ease-out;
                }
                @keyframes pickerIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default ReferencePickerModal;

